import { DateTimeUnit, DateTime as LuxonDateTime } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { accessible, comparable, localizable, manipulable } from '../componentEnhancers';
import MewsDate from '../dates/date';
import { toDateLuxon } from '../dates/dateComponent';
import { DateTimeUtc } from '../dateTimesUtc';
import { DateTimePart, ISOFormat, LocalizationMethod } from '../enums';
import Month from '../months/month';
import Time from '../times/time';
import { toTimeLuxon } from '../times/timeService';
import { getDateTimeString, toLuxon } from '../utils';
import DateTime from './dateTime';

const DATE_TIME_FORMATS = [ISOFormat.Date, ISOFormat.Seconds, ISOFormat.Milliseconds];

const PARTS = [
    DateTimePart.Year,
    DateTimePart.Month,
    DateTimePart.Week,
    DateTimePart.Date,
    DateTimePart.Day,
    DateTimePart.Hours,
    DateTimePart.Minutes,
    DateTimePart.Seconds,
    DateTimePart.Milliseconds,
] as const;

const FORMATS = [[LocalizationMethod.String, 'DateTime']] as const;

export const toDateTimeLuxon = memoize((dateTime: DateTime) => {
    type.check(dateTime, DateTime);
    return LuxonDateTime.fromMillis(dateTime.millis, { zone: dateTime.timezone });
});

const fromUtcString = (utcString: string, timezone: string) =>
    DateTime(LuxonDateTime.fromISO(utcString, { zone: timezone }));

const fromFormat = (dateString: string, dateFormatPattern: string, locale?: string) =>
    LuxonDateTime.fromFormat(dateString, dateFormatPattern, { locale });

export const DateTimeService = {
    ...comparable(toDateTimeLuxon),
    ...manipulable(DateTime, toDateTimeLuxon, PARTS),
    ...accessible(toDateTimeLuxon, PARTS),
    ...localizable(DateTime, toDateTimeLuxon, FORMATS),
    parse(dateTimeString: string, timezone: string) {
        return parseDateTimeString(dateTimeString, timezone);
    },

    /** @deprecated Use `fromUtc(appClockService.getAppNowUtc(), timezone)` */
    now(timezone: string) {
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        const luxonDate = LuxonDateTime.now().setZone(timezone);
        return DateTime(luxonDate);
    },

    fromUtcString,

    fromLocalUtcString: (utcString: string) => fromUtcString(utcString, 'utc'),

    fromUtc(dateTimeUtc: { value: string }, timezone: string) {
        type.check(dateTimeUtc, DateTimeUtc);
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        return DateTime(LuxonDateTime.fromISO(dateTimeUtc.value, { zone: timezone }));
    },

    fromFormat,

    toUtc(dateTime: DateTime) {
        const dateTimeUtc = toDateTimeLuxon(dateTime).toUTC();
        return DateTimeUtc(dateTimeUtc);
    },

    /**
     * @param dateTime - the DateTime to convert
     * @returns an ISO 8601-compliant string representation of this DateTime, including the offset, such as '-04:00'.
     */
    toISOString(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime);
        return luxonDateTime.toISO();
    },

    fromJavascriptDate(dateTime: Date, timezone: string) {
        type.check(dateTime, Date);
        check.timezone(timezone);
        check.notUtcTimezone(timezone);

        return parseDateTimeString(getDateTimeString(dateTime), timezone);
    },

    fromMonth(monthString: string, timezone: string) {
        const luxon = toLuxon(monthString, ISOFormat.Month, timezone);
        return DateTime(luxon);
    },

    toYearMonth(dateTime: DateTime) {
        const luxonDate = toDateTimeLuxon(dateTime);
        return Month(luxonDate).value;
    },

    toJavascriptDate(dateTime: DateTime) {
        const luxonDate = toDateTimeLuxon(dateTime);
        // Month in JS Date start with zero, Luxon calculates it from 1.
        return new Date(
            luxonDate.year,
            luxonDate.month - 1,
            luxonDate.day,
            luxonDate.hour,
            luxonDate.minute,
            luxonDate.second,
            luxonDate.millisecond
        );
    },

    fromDate(date: MewsDate) {
        const luxonDate = toDateLuxon(date);
        return DateTime(luxonDate);
    },

    toDate: (dateTime: DateTime) => {
        const luxonDate = toDateTimeLuxon(dateTime);
        return MewsDate(luxonDate);
    },

    fromDateAndTime(date: MewsDate, time: { value: string }) {
        const luxonDate = toDateLuxon(date);
        const luxonTime = toTimeLuxon(time);
        const finalLuxonDate = luxonDate.set({
            hour: luxonTime.hour,
            minute: luxonTime.minute,
            second: luxonTime.second,
        });

        return DateTime(finalLuxonDate);
    },

    toTime(dateTime: DateTime) {
        const luxonDate = toDateTimeLuxon(dateTime);
        return Time(luxonDate);
    },

    toMonth(date: DateTime) {
        return Month(toDateTimeLuxon(date));
    },

    valueOf(dateTime: DateTime) {
        return toDateTimeLuxon(dateTime).valueOf();
    },

    /** @deprecated */
    stripTime(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).startOf('day');
        return DateTime(luxonDateTime);
    },

    startOfDay(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).startOf('day');
        return DateTime(luxonDateTime);
    },

    endOfDay(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).endOf('day');
        return DateTime(luxonDateTime);
    },

    endOfMonth(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).endOf('month');
        return DateTime(luxonDateTime);
    },

    startOfMonth(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).startOf('month');
        return DateTime(luxonDateTime);
    },

    startOfHour(dateTime: DateTime) {
        const luxonDateTime = toDateTimeLuxon(dateTime).startOf('hour');
        return DateTime(luxonDateTime);
    },

    startOf: (part: DateTimeUnit) => (dateTime: DateTime) => {
        const luxonDateTime = toDateTimeLuxon(dateTime).startOf(part);
        return DateTime(luxonDateTime);
    },

    endOf: (part: DateTimeUnit) => (dateTime: DateTime) => {
        const luxonDateTime = toDateTimeLuxon(dateTime).endOf(part);
        return DateTime(luxonDateTime);
    },
    daysInMonth: (dateTime: DateTime) => {
        const month = DateTimeService.getMonth(dateTime);
        const year = DateTimeService.getYear(dateTime);
        return LuxonDateTime.local(year, month + 1).daysInMonth;
    },
};

function parseDateTimeString(dateTimeString: string, timezone: string, formats = DATE_TIME_FORMATS) {
    check.string(dateTimeString);
    check.timezone(timezone);
    check.notUtcTimezone(timezone);

    const luxonDate = toLuxon(dateTimeString, formats, timezone);
    return DateTime(luxonDate);
}
